import { copyImageDataRect } from '~/lib/utils';
import type { Frame, MatchResult, ReferencePatch } from '~/types/pipeline';
import { templateMatchViaWorker } from './match';

export function patchCenterOffsetInPatchFrame(
  patch: ReferencePatch,
  patchFrameCellRect: { x: number; y: number },
): { x: number; y: number } {
  return {
    x: patch.x - patchFrameCellRect.x,
    y: patch.y - patchFrameCellRect.y,
  };
}

export function canTemplateFitInFrame(frame: ImageData, patch: ImageData): boolean {
  if (patch.width <= 0 || patch.height <= 0) return false;
  return frame.width >= patch.width && frame.height >= patch.height;
}

export function extractPatchImageData(img: ImageData, patch: ReferencePatch): ImageData {
  // The patch may straddle the source image edge; pixels outside source default
  // to (0,0,0,0) — left untouched in the freshly-allocated ImageData.
  const radius = Math.round(patch.radius);
  const size = radius * 2;
  const x0 = Math.round(patch.x - radius);
  const y0 = Math.round(patch.y - radius);
  const xStart = Math.max(0, x0);
  const yStart = Math.max(0, y0);
  const xEnd = Math.min(img.width, x0 + size);
  const yEnd = Math.min(img.height, y0 + size);
  const out = new ImageData(size, size);
  if (xStart >= xEnd || yStart >= yEnd) return out;

  const rowBytes = (xEnd - xStart) * 4;
  for (let row = yStart; row < yEnd; row++) {
    const srcStart = (row * img.width + xStart) * 4;
    const dstStart = ((row - y0) * size + (xStart - x0)) * 4;
    out.data.set(img.data.subarray(srcStart, srcStart + rowBytes), dstStart);
  }
  return out;
}

export function extractCellImageData(
  img: ImageData,
  cell: { x: number; y: number; w: number; h: number },
): ImageData {
  return copyImageDataRect(img, cell);
}

export async function runMatches(
  cleaned: ImageData,
  frames: readonly Frame[],
  patch: ReferencePatch,
): Promise<MatchResult[]> {
  if (!isPatchFrameIndexValid(frames, patch)) return [];
  const patchFrame = frames[patch.frameIndex];
  if (!patchFrame) return [];

  const patchImage = extractPatchImageData(cleaned, patch);
  const previousCenter = patchCenterOffsetInPatchFrame(patch, patchFrame.cellRect);
  const out: MatchResult[] = [];
  for (const frame of frames) {
    const frameImage = extractCellImageData(cleaned, frame.cellRect);
    if (!canTemplateFitInFrame(frameImage, patchImage)) {
      out.push({ frameIndex: frame.index, dx: 0, dy: 0, confidence: 0 });
      continue;
    }
    const searchRadius = Math.round(0.2 * Math.min(frame.cellRect.w, frame.cellRect.h));
    const result = await templateMatchViaWorker(
      frameImage,
      patchImage,
      previousCenter,
      searchRadius,
    );
    out.push({ frameIndex: frame.index, ...result });
  }
  return out;
}

export function isPatchFrameIndexValid(frames: readonly Frame[], patch: ReferencePatch): boolean {
  return patch.frameIndex >= 0 && patch.frameIndex < frames.length;
}
